import { Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { date, object, ref, type Schema } from 'yup';

import SkeletonLoader from '@components/SkeletonLoader';

import { useHealthChart } from '@hooks/useHealthChart';
import { useIsMobile } from '@hooks/useIsMobile';

import Alerts from './Alerts';
import DataVisual from './DataVisual';
import Filters from './Filters';
import Kpi from './Kpi';
import SourceBrands from './SourceBrands';

const messages = defineMessages({
  required: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  endBeforeStart: {
    id: 'GLOBAL.dateValidation.endBeforeStart',
    defaultMessage: 'End date cannot be before start date',
  },
  startAfterEnd: {
    id: 'GLOBAL.dateValidation.startAfterEnd',
    defaultMessage: 'Start date cannot be after end date',
  },
  endFutureDate: {
    id: 'GLOBAL.dateValidation.endFutureDate',
    defaultMessage: 'End date cannot be a future date',
  },
  startFutureDate: {
    id: 'GLOBAL.dateValidation.startFutureDate',
    defaultMessage: 'Start date cannot be a future date',
  },
  invalidDate: {
    id: 'GLOBAL.dateValidation.invalidDate',
    defaultMessage: 'Invalid date',
  },
  table: {
    id: 'healthTracker.detail.table',
    defaultMessage: 'Table',
  },
  graph: {
    id: 'healthTracker.detail.graph',
    defaultMessage: 'Graph',
  },
});

type FormValues = {
  date_from: Date;
  date_to: Date;
  submit: string | null;
};

export default function DrilldownContent() {
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const { isInitialized, maxDate, minDate, handleDateChange } =
    useHealthChart();

  const initialValues: FormValues = {
    date_from: minDate,
    date_to: maxDate,
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    date_from: date()
      .max(ref('date_to'), formatMessage(messages.startAfterEnd))
      .typeError(formatMessage(messages.invalidDate))
      .required(formatMessage(messages.required)),
    date_to: date()
      .max(new Date(), formatMessage(messages.endFutureDate))
      .min(ref('date_from'), formatMessage(messages.endBeforeStart))
      .typeError(formatMessage(messages.invalidDate))
      .required(formatMessage(messages.required)),
  });

  // Range for custom dates is 1
  const handleSubmit = async (values: FormValues) => {
    handleDateChange(undefined, values.date_from, values.date_to, 1);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {() => (
        <Form>
          <SkeletonLoader loading={!isInitialized} height={500}>
            <Alerts />
            <Stack spacing={1}>
              {isMobile && <Filters />}
              <SourceBrands />
              <Stack
                direction="column"
                flexDirection={isMobile ? 'column' : 'column-reverse'}
                rowGap={2}
                pb={2}
              >
                <DataVisual />
                <Kpi />
              </Stack>
            </Stack>
          </SkeletonLoader>
        </Form>
      )}
    </Formik>
  );
}
